import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import catalogService from "../../services/catalogService";
import { ICatalog, ICatalogState } from "Interfaces/interface.catalog";
import { extractErrorMessage } from "utils/ErrorHandlers";
import toast from "react-hot-toast";

const initialState: ICatalogState = {
  catalogs: [],
  loading: false,
  error: null,
  isCatalogLoaded: false,
  catalogFetchError: null,
};

export const fetchUserCatalogs = createAsyncThunk<
  ICatalog[],
  void,
  { rejectValue: string }
>("catalog/fetchUserCatalogs", async (_, { rejectWithValue }) => {
  try {
    const response = await catalogService.userCatalogs();
    return response;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message || "Failed to fetch catalogs");
    return rejectWithValue(message || "Failed to fetch catalogs");
  }
});

export const deleteUserCatalog = createAsyncThunk<
  ICatalog[], // Return updated catalogs
  string, // catalogId
  { rejectValue: string }
>("catalog/deleteUserCatalog", async (catalogId, { rejectWithValue }) => {
  try {
    const response = await catalogService.deleteCatalog(catalogId);
    toast.success("Catalog deleted successfully");
    return response.updatedCatalogs;
  } catch (error: any) {
    const message = extractErrorMessage(error);
    toast.error(message || "Failed to delete catalog");
    return rejectWithValue(message || "Failed to delete catalog");
  }
});

const catalogSlice = createSlice({
  name: "catalog",
  initialState,
  reducers: {
    clearCatalogState: (state) => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchUserCatalogs.pending, (state) => {
        state.loading = true;
        state.catalogFetchError = null;
      })
      .addCase(fetchUserCatalogs.fulfilled, (state, action) => {
        state.catalogs = action.payload;
        state.loading = false;
        state.isCatalogLoaded = true;
      })
      .addCase(fetchUserCatalogs.rejected, (state, action) => {
        state.loading = false;
        state.catalogFetchError = action.payload as string;
      })
      .addCase(deleteUserCatalog.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteUserCatalog.fulfilled, (state, action) => {
        state.catalogs = action.payload;
        state.loading = false;
        state.isCatalogLoaded = false;
      })
      .addCase(deleteUserCatalog.rejected, (state, action) => {
        state.error = action.payload as string;
        state.loading = false;
      });
  },
});

export const { clearCatalogState } = catalogSlice.actions;
export default catalogSlice.reducer;

export const selectUserCatalogs = (state: any) => state.catalog.catalogs || [];
export const selectCatalogFetchError = (state: any) =>
  state.catalog.catalogFetchError;
export const selectUserCatalogsLoading = (state: any) => state.catalog.loading;
export const selectUserCatalogsLoaded = (state: any) =>
  state.catalog.isCatalogLoaded;
export const selectUserCatalogsError = (state: any) => state.catalog.error;
